import { contributionForGap, currentTopUpAmount, dispatchPanelCosts } from "./contribution";
import {
  refreshVolBoughtValidation,
  syncVolDeclaredDataset,
} from "./vol_bought_validation";

function normalizeGapTarget(raw) {
  const value = parseFloat(raw);
  if (Number.isNaN(value)) return 0;

  return Math.min(100, Math.max(0, value));
}

export function formatNumber(value) {
  const number = Number(value) || 0;
  if (Math.abs(number - Math.round(number)) < 1e-9) {
    return Math.round(number).toLocaleString();
  }

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatPercent(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return "0%";

  return `${number.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

// Keep in sync with LivingWageGapCalculation::GAP_PER_TRADE_UNIT_PRECISION
export const GTU_PRECISION = 6;

function formatGtu(value) {
  if (value == null) return "—";

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: GTU_PRECISION,
    maximumFractionDigits: GTU_PRECISION,
  });
}

function currentCurrencyDisplay() {
  const raw = document.getElementById("scenario_currency_display")?.value;
  if (raw === "facility" || raw === "local") return "facility";
  return "payment";
}

function countryContributionGap(country) {
  if (!country) return 0;

  // Raw country rows keep gap_payment as the full unprorated living-wage gap.
  // Contribution totals must use the buying-leverage-prorated amount.
  if (country.prorated_gap_payment != null && country.prorated_gap_payment !== "") {
    return Number(country.prorated_gap_payment) || 0;
  }

  return Number(country.gap_payment) || 0;
}

function panelCountriesLookup(panel) {
  const lookup = { ...(panel?.countries || {}) };

  const indexCountry = (country) => {
    if (!country) return;

    const code = country.code?.toString().trim() || "unknown";
    const merged = { ...(lookup[code] || {}), ...country };
    merged.gap_payment = countryContributionGap(merged);
    lookup[code] = merged;
    if (country.name) lookup[`name:${country.name}`] = { ...(lookup[`name:${country.name}`] || {}), ...merged };
  };

  Object.values(panel?.countries || {}).forEach(indexCountry);
  (panel?.countries_list || []).forEach(indexCountry);

  return lookup;
}

function countryForCell(countries, cell) {
  const code = cell.dataset.countryCode || "unknown";
  const name = cell.dataset.countryName;

  return countries[code] || (name ? countries[`name:${name}`] : undefined);
}

function byCountryAllLookup(rows) {
  return Object.fromEntries((rows || []).map((row) => [row.name, row]));
}

function facilityGap(facility, showFacilityCurrency) {
  if (!facility || facility.selected === false) return 0;

  if (showFacilityCurrency) return facility.gap_local || 0;

  return facility.gap_payment ?? 0;
}

function facilityCurrency(facility, showFacilityCurrency, paymentCurrency) {
  if (showFacilityCurrency) return facility?.local_currency_code;

  return facility?.payment_currency_code || paymentCurrency;
}

function setCellAmount(cell, value) {
  const amount = cell?.querySelector("[data-role='amount']");
  if (amount) {
    amount.textContent = value;
    return;
  }

  if (cell) cell.textContent = value;
}

function updatableNodes() {
  const selectors = [
    "[data-scenario-modeling-amount]",
    "[data-scenario-modeling-summary-target='countryBuyingPct']",
    "[data-scenario-modeling-intermediaries-target='salaryMatricesCell']",
    "[data-scenario-modeling-intermediaries-target='workersCell']",
    "[data-scenario-modeling-by-country-summary-target='buyingPctCell']",
    "[data-scenario-modeling-facility-selection-target='buyingPctCell']",
  ];

  return [ ...document.querySelectorAll(selectors.join(",")) ];
}

export function showModelingLoading() {
  updatableNodes().forEach((node) => {
    if (node.dataset.loading === "true") return;

    node.dataset.loading = "true";
    // Preserve markup (e.g. nested currency spans) so we can restore if the payload
    // path does not refresh this node.
    node.dataset.storedValue = node.innerHTML;
    node.textContent = "…";
    node.classList.add("animate-pulse", "text-gray-modern-400");
  });
}

export function hideModelingLoading() {
  updatableNodes().forEach((node) => {
    if (node.dataset.loading !== "true") return;

    node.classList.remove("animate-pulse", "text-gray-modern-400");

    // Always restore pre-loading content. Callers that refresh from a payload must
    // apply that payload AFTER hideModelingLoading, otherwise unupdated nodes
    // (and any race with event listeners) stay stuck on the "…" placeholder.
    if (node.dataset.storedValue != null) {
      node.innerHTML = node.dataset.storedValue;
    }

    delete node.dataset.loading;
    delete node.dataset.storedValue;
  });
}

function updateSummaryPanels(payload, gapTarget) {
  document.querySelectorAll("[data-scenario-modeling-summary-panel-id-value]").forEach((element) => {
    const panelId = element.dataset.scenarioModelingSummaryPanelIdValue;
    const panel = payload.panels?.[panelId];
    if (!panel) return;

    const countries = panelCountriesLookup(panel);

    element.querySelectorAll("[data-scenario-modeling-summary-target='totalContribution']").forEach((node) => {
      const base = contributionForGap(panel.total_gap_payment, gapTarget, panel.taxes_levies, panel.other_costs);
      if (node.hasAttribute("data-scenario-total-with-add-on")) {
        node.dataset.baseContribution = String(base);
        node.textContent = formatNumber(base + currentTopUpAmount());
        return;
      }

      node.textContent = formatNumber(base);
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='salaryMatrices']").forEach((node) => {
      if (panel.salary_matrices != null) node.textContent = formatNumber(panel.salary_matrices);
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='workersReceiving']").forEach((node) => {
      if (panel.workers_receiving_contribution != null) {
        node.textContent = formatNumber(panel.workers_receiving_contribution);
      }
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='volDeclared']").forEach((node) => {
      if (panel.vol_declared != null) node.textContent = formatNumber(panel.vol_declared);
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='countryVolDeclared']").forEach((cell) => {
      const country = countryForCell(countries, cell);
      setCellAmount(cell, formatNumber(country?.vol_declared || 0));
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='countryBuyingPct']").forEach((cell) => {
      const country = countryForCell(countries, cell);
      cell.textContent = formatPercent(country?.buying_pct || 0);
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='countryGtu']").forEach((cell) => {
      const country = countryForCell(countries, cell);
      setCellAmount(cell, formatGtu(country?.gtu));
    });

    element.querySelectorAll("[data-scenario-modeling-summary-target='countryContribution']").forEach((cell) => {
      const country = countryForCell(countries, cell);
      const gap = countryContributionGap(country);
      cell.dataset.gapPayment = String(gap);
      const amount = cell.querySelector("[data-role='amount']");
      if (amount) amount.textContent = formatNumber(contributionForGap(gap, gapTarget));
    });
  });
}

function updateFacilityTableRows(payload, gapTarget, currencyDisplay) {
  const showFacilityCurrency = currencyDisplay === "facility";
  const facilities = payload.facilities || {};
  const paymentCurrency = payload.currency_code || "";

  document.querySelectorAll("[data-scenario-modeling-summary-target='contributionCell']").forEach((cell) => {
    const facility = facilities[cell.dataset.facilityId];
    if (!facility) return;

    const gap = facilityGap(facility, showFacilityCurrency);
    const leverage = facility.selected === false ? 0 : Number(facility.buying_leverage) || 0;
    cell.dataset.gapFacility = String(facility.selected === false ? 0 : facility.gap_local || 0);
    cell.dataset.gapPayment = String(facility.selected === false ? 0 : facility.gap_payment ?? 0);
    cell.dataset.buyingLeverage = String(leverage);

    const amount = cell.querySelector("[data-role='amount']");
    if (amount) amount.textContent = formatNumber(contributionForGap(gap, gapTarget, 0, 0, leverage));

    const currency = cell.querySelector("[data-role='currency']");
    if (currency) currency.textContent = facilityCurrency(facility, showFacilityCurrency, paymentCurrency) || "";
  });

  document.querySelectorAll("[data-scenario-modeling-summary-target='meanCell']").forEach((cell) => {
    const facility = facilities[cell.dataset.facilityId];
    if (!facility) return;

    const gap = facilityGap(facility, showFacilityCurrency);
    const leverage = facility.selected === false ? 0 : Number(facility.buying_leverage) || 0;
    const workers = Number(cell.dataset.workers) || facility.workers || 0;
    const contribution = contributionForGap(gap, gapTarget, 0, 0, leverage);
    const mean = workers > 0 ? Math.round(contribution / workers) : 0;

    cell.dataset.gapFacility = String(facility.selected === false ? 0 : facility.gap_local || 0);
    cell.dataset.gapPayment = String(facility.selected === false ? 0 : facility.gap_payment ?? 0);
    cell.dataset.buyingLeverage = String(leverage);

    const amount = cell.querySelector("[data-role='amount']");
    if (amount) amount.textContent = formatNumber(mean);

    const currency = cell.querySelector("[data-role='currency']");
    const currencyCode = facilityCurrency(facility, showFacilityCurrency, paymentCurrency) || "";
    if (currency) currency.textContent = currencyCode ? ` ${currencyCode}` : "";
  });

  document.querySelectorAll("[data-scenario-modeling-summary-target='gtuCell']").forEach((cell) => {
    const facilityCell = cell.closest("tr")?.querySelector("[data-facility-id]");
    const facility = facilityCell ? facilities[facilityCell.dataset.facilityId] : null;
    if (facility) {
      const gtu = showFacilityCurrency ? facility.gtu_local : facility.gtu_payment;
      setCellAmount(cell, formatGtu(gtu));
      if (facility.gtu_local != null) cell.dataset.gtuLocal = String(facility.gtu_local);
      if (facility.gtu_payment != null) cell.dataset.gtuPayment = String(facility.gtu_payment);
    }

    const currency = cell.querySelector("[data-role='currency']");
    if (!currency) return;

    currency.textContent = facility
      ? facilityCurrency(facility, showFacilityCurrency, paymentCurrency) || currency.textContent
      : currency.textContent;
  });
}

function costInputsForUser(userId, payloadRow) {
  const taxesInput = document.querySelector(
    `input[name="scenario[intermediary_modeling_inputs][${userId}][taxes_levies]"], input[name="intermediary_modeling_inputs[taxes_levies]"]`,
  );
  const otherInput = document.querySelector(
    `input[name="scenario[intermediary_modeling_inputs][${userId}][other_costs]"], input[name="intermediary_modeling_inputs[other_costs]"]`,
  );

  if (taxesInput || otherInput) {
    return {
      taxes: Number(taxesInput?.value) || 0,
      other: Number(otherInput?.value) || 0,
    };
  }

  return {
    taxes: Number(payloadRow?.taxes_levies) || 0,
    other: Number(payloadRow?.other_costs) || 0,
  };
}

function updateIntermediariesTable(payload, gapTarget) {
  const intermediaries = payload.intermediaries || {};
  const totals = payload.intermediaries_totals || {};
  let totalTaxes = 0;
  let totalOther = 0;

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='livingWageContributionCell']").forEach((cell) => {
    const row = intermediaries[cell.dataset.userId];
    const gap = Number(row?.gap_payment ?? cell.dataset.gapPayment) || 0;
    cell.dataset.gapPayment = String(gap);
    const amount = cell.querySelector("[data-role='amount']");
    if (amount) {
      amount.textContent = formatNumber(contributionForGap(gap, gapTarget, 0, 0));
    }
  });

  const panelCosts = [];
  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='contributionCell']").forEach((cell) => {
    const row = intermediaries[cell.dataset.userId];
    const gap = Number(row?.gap_payment ?? cell.dataset.gapPayment) || 0;
    const costs = costInputsForUser(cell.dataset.userId, row);
    totalTaxes += costs.taxes;
    totalOther += costs.other;
    panelCosts.push({ panelId: cell.dataset.panelId, ...costs });
    cell.dataset.gapPayment = String(gap);
    const amount = cell.querySelector("[data-role='amount']");
    if (amount) {
      amount.textContent = formatNumber(contributionForGap(gap, gapTarget, costs.taxes, costs.other));
    }
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='salaryMatricesCell']").forEach((cell) => {
    const row = intermediaries[cell.dataset.userId];
    cell.textContent = formatNumber(row?.salary_matrices || 0);
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='workersCell']").forEach((cell) => {
    const row = intermediaries[cell.dataset.userId];
    cell.textContent = formatNumber(row?.workers_receiving_contribution || 0);
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='volDeclaredCell']").forEach((cell) => {
    const row = intermediaries[cell.dataset.userId];
    setCellAmount(cell, formatNumber(row?.vol_declared || 0));

    const input = document.querySelector(
      `[data-scenario-modeling-intermediaries-target='volBoughtInput'][data-user-id='${cell.dataset.userId}']`,
    );
    if (input) input.dataset.volDeclared = String(row?.vol_declared || 0);
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='livingWageContributionValue']").forEach((node) => {
    const gap = payload.intermediaries_total_gap_payment || 0;
    node.textContent = formatNumber(contributionForGap(gap, gapTarget, 0, 0));
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='totalContributionValue']").forEach((node) => {
    const gap = payload.intermediaries_total_gap_payment || 0;
    const base = contributionForGap(gap, gapTarget, totalTaxes, totalOther);
    node.dataset.gapPayment = String(gap);
    node.dataset.baseContribution = String(base);
    node.textContent = formatNumber(base + currentTopUpAmount());
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='salaryMatricesValue']").forEach((node) => {
    if (totals.salary_matrices != null) node.textContent = formatNumber(totals.salary_matrices);
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='workersValue']").forEach((node) => {
    if (totals.workers_receiving_contribution != null) {
      node.textContent = formatNumber(totals.workers_receiving_contribution);
    }
  });

  document.querySelectorAll("[data-scenario-modeling-intermediaries-target='volDeclaredValue']").forEach((node) => {
    if (totals.vol_declared != null) node.textContent = formatNumber(totals.vol_declared);
  });

  syncVolDeclaredDataset();
  refreshVolBoughtValidation();

  dispatchPanelCosts(panelCosts);
}

function updateByCountrySummarySections(payload, gapTarget) {
  const byName = byCountryAllLookup(payload.by_country_all);

  document.querySelectorAll("[data-controller~='scenario-modeling-by-country-summary']").forEach((section) => {
    section.querySelectorAll("[data-scenario-modeling-by-country-summary-target='volDeclaredCell']").forEach((cell) => {
      const row = byName[cell.dataset.countryName];
      setCellAmount(cell, formatNumber(row?.vol_declared || 0));
    });

    section.querySelectorAll("[data-scenario-modeling-by-country-summary-target='buyingPctCell']").forEach((cell) => {
      const row = byName[cell.dataset.countryName];
      cell.textContent = formatPercent(row?.buying_pct || 0);
    });

    section.querySelectorAll("[data-scenario-modeling-by-country-summary-target='gtuCell']").forEach((cell) => {
      const row = byName[cell.dataset.countryName];
      setCellAmount(cell, formatGtu(row?.gtu));
    });

    section.querySelectorAll("[data-scenario-modeling-by-country-summary-target='contributionCell']").forEach((cell) => {
      const row = byName[cell.dataset.countryName];
      const gap = countryContributionGap(row);
      cell.dataset.gapPayment = String(gap);
      const amount = cell.querySelector("[data-role='amount']");
      if (amount) amount.textContent = formatNumber(contributionForGap(gap, gapTarget));
    });
  });
}

function updateFacilitySelectionBadges(payload) {
  document.querySelectorAll("[data-scenario-modeling-facility-selection-panel-id-value]").forEach((element) => {
    const panelId = element.dataset.scenarioModelingFacilitySelectionPanelIdValue;
    const panel = payload.panels?.[panelId];
    const badge = element.querySelector("[data-scenario-modeling-facility-selection-target='selectedCountBadge']");
    const template =
      element.dataset.scenarioModelingFacilitySelectionSelectedCountTemplateValue || "%{count} Selected";

    if (badge && panel?.facilities_selected_count != null) {
      badge.textContent = template.replace("%{count}", String(panel.facilities_selected_count));
    }

    element.querySelectorAll("[data-scenario-modeling-facility-selection-target='buyingPctCell']").forEach((cell) => {
      const facility = payload.facilities?.[cell.dataset.facilityId];
      cell.textContent = formatPercent(facility?.buying_pct || 0);
    });
  });
}

export function applyModelingPayload(payload, { gapTarget, currencyDisplay } = {}) {
  if (!payload) return;

  const resolvedGapTarget =
    gapTarget ??
    normalizeGapTarget(payload.gap_target) ??
    normalizeGapTarget(document.getElementById("scenario_lw_gap_closure_target")?.value);

  const resolvedCurrencyDisplay = currencyDisplay || currentCurrencyDisplay();

  updateSummaryPanels(payload, resolvedGapTarget);
  updateIntermediariesTable(payload, resolvedGapTarget);
  updateByCountrySummarySections(payload, resolvedGapTarget);
  updateFacilitySelectionBadges(payload);
  updateFacilityTableRows(payload, resolvedGapTarget, resolvedCurrencyDisplay);
}

export function dispatchModelingPayload(payload, { gapTarget, currencyDisplay } = {}) {
  applyModelingPayload(payload, { gapTarget, currencyDisplay });

  window.dispatchEvent(
    new CustomEvent("scenario-modeling:payload-applied", {
      detail: { payload, gapTarget, currencyDisplay },
    })
  );
}
